/**
 * Governance API client — calls Imagia's partner governance endpoints.
 */

const IMAGIA_API = import.meta.env.VITE_IMAGIA_API_URL || 'https://imagia.net';
const PARTNER_KEY = import.meta.env.VITE_IMAGIA_PARTNER_KEY || '';

const headers = () => ({
  'X-Partner-Key': PARTNER_KEY,
  'Content-Type': 'application/json',
});

async function api(method, path, body, params) {
  const url = new URL(`${IMAGIA_API}/api/partner/governance${path}`);
  if (params) Object.entries(params).forEach(([k, v]) => v != null && url.searchParams.set(k, v));
  const opts = { method, headers: headers() };
  if (body) opts.body = JSON.stringify(body);
  const res = await fetch(url, opts);
  if (!res.ok) throw new Error(`API error: ${res.status}`);
  return res.json();
}

export const governance = {
  // Committees
  getCommittees: (userEmail) => api('GET', '/committees', null, { user_email: userEmail }),
  getCommittee: (id) => api('GET', `/committees/${id}`),
  joinCommittee: (id, userEmail, applicationText) => api('POST', `/committees/${id}/join`, { user_email: userEmail, application_text: applicationText }),
  approveJoin: (id, targetUserId, userEmail) => api('POST', `/committees/${id}/approve-join`, { user_email: userEmail, target_user_id: targetUserId }),

  // Items
  getItems: (params) => api('GET', '/items', null, params),
  getItem: (id) => api('GET', `/items/${id}`),
  createItem: (data) => api('POST', '/items', data),
  addComment: (id, userEmail, content, attachments, links) => api('POST', `/items/${id}/comment`, { user_email: userEmail, content, attachments, links }),
  upvoteComment: (itemId, commentId, userEmail) => api('POST', `/items/${itemId}/upvote-comment/${commentId}`, { user_email: userEmail }),
  moveToVote: (id) => api('POST', `/items/${id}/move-to-vote`),
  castVote: (id, userEmail, vote, reason) => api('POST', `/items/${id}/vote`, { user_email: userEmail, vote, reason }),
  adminApprove: (id, userEmail) => api('POST', `/items/${id}/approve`, { user_email: userEmail }),
  setRole: (committeeId, userEmail, role) => api('POST', `/committees/${committeeId}/set-role`, { user_email: userEmail, role }),

  // Actions
  getActions: () => api('GET', '/actions'),
  assignAction: (id, userEmail) => api('POST', `/actions/${id}/assign`, { user_email: userEmail }),
  completeAction: (id, notes) => api('POST', `/actions/${id}/complete`, { notes }),
  setActionStatus: (id, status) => {
    const url = new URL(`${IMAGIA_API}/api/partner/governance/items/${id}/set-action-status`);
    return fetch(url, { method: 'POST', headers: headers(), body: JSON.stringify({ action_status: status }) }).then(r => r.json());
  },

  // AI Agent builds (via Imagia partner builds API)
  triggerAiBuild: (item, sourceApp = 'jubly') => {
    // Clean the description — strip DOM context, extract actionable info
    let desc = item.description || item.title || '';
    // Extract user's actual issue from DOM-context-filled descriptions
    const issueMatch = desc.match(/(?:The issue is:|the issue is:)\s*(.*)/s);
    if (issueMatch) desc = issueMatch[1].trim();
    desc = desc.replace(/<[^>]+>/g, '').replace(/Selected area contains:[\s\S]*?(?=The issue is:|$)/gi, '').replace(/\d+\s*[×x]\s*\d+/g, '').replace(/\|/g, ' ').replace(/\s{2,}/g, ' ').trim();

    const title = item.title?.replace(/^(Bug|Report) on /, '').replace(/Selected area contains:.*$/i, '').trim() || 'Fix issue';

    const url = new URL(`${IMAGIA_API}/api/partner/builds`);
    return fetch(url, {
      method: 'POST', headers: headers(),
      body: JSON.stringify({
        project_slug: sourceApp,
        title,
        description: `[${item.type?.toUpperCase() || 'TASK'}] ${title}\n\n${desc}\n\nPriority: ${item.priority || 'medium'}`,
        user_email: 'governance@jubly.net',
        contribution_type: item.type === 'bug' ? 'bug_fix' : 'feature',
        source_app: sourceApp,
        source_ref: item.id,
        governance_item_id: item.id,
      }),
    }).then(r => r.json());
  },
};

export default governance;
